import { Component, Inject, OnInit } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import errorUtils from "@app/_classes/error-utils";
import { colorToText, hexTextValidator, textToColor } from "@app/_helpers/color";
import { hasPermission } from "@app/_helpers/utils";
import { CustomValidators } from "@app/_validators/custom-validators";
import { AppService } from "@app/app.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TagsQuery, TagsService, TagType, UserSettingsQuery } from "@timeghost/web-api";
import { BehaviorSubject } from "rxjs";
import { distinctUntilChanged, finalize, map } from "rxjs/operators";

@UntilDestroy()
@Component({
	selector: "app-create-tag",
	templateUrl: "./create-tag.component.html",
	styleUrls: ["./create-tag.component.scss"],
})
export class CreateTagComponent implements OnInit {
	group = new UntypedFormGroup(
		{
			name: new UntypedFormControl("", [Validators.required, CustomValidators.minLength(3)]),
			tagType: new UntypedFormControl(TagType.Time, [Validators.required]),
			color: new UntypedFormControl(""),
			colorText: new UntypedFormControl("", [Validators.maxLength(7), (ctrl) => hexTextValidator(ctrl?.value)]),
		},
		{
			validators: [
				({ root: ctrl }) => {
					const ctrls = (ctrl as UntypedFormGroup).controls;
					if (
						this.tagsQuery.getCount(
							(x) =>
								x.tagType === TagType.Time &&
								x.name?.toLowerCase() === `${ctrls.name.value}`.toLowerCase()
						) > 0
					) {
						ctrls.name.setErrors({ exists: true });
						return { exists: true };
					}
					return null;
				},
			],
			updateOn: "change",
		}
	);
	readonly workspace$createPermission = this.userSettingsQuery.select().pipe(
		map((userSettings) => {
			return !!userSettings.workspace.permissionSettings?.groupsCanManageTags?.find((x) =>
				hasPermission(x.id, userSettings)
			);
		})
	);
	private _isLoading = new BehaviorSubject<boolean>(false);
	readonly isLoading$ = this._isLoading.asObservable().pipe(distinctUntilChanged());
	get isLoading() {
		return this._isLoading.getValue();
	}
	set isLoading(val: boolean) {
		this._isLoading.next(val);
	}

	constructor(
		private tagService: TagsService,
		private tagsQuery: TagsQuery,
		private ref: MatDialogRef<CreateTagComponent>,
		@Inject(MAT_DIALOG_DATA)
		private data: { name?: string; type?: TagType; allowTypeModify?: boolean },
		private appService: AppService,
		private userSettingsQuery: UserSettingsQuery
	) {}

	ngOnInit() {
		this.ref.updateSize("500px");
		this.ref.addPanelClass(["mat-dialog-vanilla", "mat-dialog-relative"]);
		this.group.patchValue({
			name: this.data?.name ?? "",
			tagType: this.data?.type ?? TagType.Time,
		});
		const initial = this.appService.colorScheme.getColorFromScheme(this.tagsQuery.getCount() || 1);
		this.group.patchValue({
			color: initial,
			colorText: colorToText(initial),
		});
		if (this.data.allowTypeModify === false) this.group.controls.tagType.disable();

		this.group.controls.colorText.valueChanges.pipe(untilDestroyed(this)).subscribe((txt) => {
			const parsed = textToColor(txt);
			if (parsed) this.group.patchValue({ color: parsed }, { emitEvent: false });
		});
	}
	onColorPicked(color: string) {
		const text = colorToText(color);
		const parsed = textToColor(text);
		if (!parsed) return;
		this.group.patchValue(
			{
				color: parsed,
				colorText: text,
			},
			{ emitEvent: false }
		);
		this.group.updateValueAndValidity();
	}
	get formNameErrors() {
		if (this.group.controls.name.errors?.required) return { content: "errors.required", args: { field: "Name" } };
		if (this.group.controls.name.errors?.minlength)
			return { content: "errors.minlength", args: { field: "Name", length: 3 } };
		if (this.group.controls.name.errors?.exists) return { content: "errors.unique", args: { field: "Name" } };
		return null;
	}
	createNewTag() {
		this.isLoading = true;
		const value = this.group.getRawValue();
		this.tagService
			.add({ name: value.name, tagType: value.tagType, color: value.color } as any)
			.pipe(
				finalize(() => {
					this.isLoading = false;
				})
			)
			.subscribe(
				(tag) => this.ref.close(tag),
				(err) => {
					if (errorUtils.isUniqueError(err)) {
						this.group.controls.name.setErrors({
							exists: true,
						});
						this.isLoading = false;
					}
				}
			);
	}
}
